import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { catchError, from, fromEvent, map, Observable, of, switchMap, timeout } from 'rxjs';
import { first } from 'rxjs/operators';

import { isAuthenticatedGuard } from './auth.guard';
import { AuthenticationService } from '../service/authentication.service';

export const useJWTTokenAuthGuard: () => Observable<boolean | UrlTree> = () => {
  const router = inject(Router);
  const authService: AuthenticationService = inject(AuthenticationService);

  const queryParams = router.getCurrentNavigation()?.initialUrl.queryParams;
  const useJWTToken = queryParams?.['useJWTToken'];
  const useAuthToken = queryParams?.['useAuthToken'];
  const additionalScopes = queryParams?.['scope']?.split(' ');

  if (!queryParams || (!useJWTToken && !useAuthToken)) {
    return isAuthenticatedGuard();
  } else {
    const loginUrlTree = router.createUrlTree(['login'], { queryParams });

    // Send a message to the parent window to get the token (window.opener if the app is opened in a new tab, window.parent if the app is opened in an iframe)
    if (window.opener) {
      window?.opener?.postMessage({ getToken: true }, '*'); // NOSONAR typescript:S2819
    } else if (window?.parent !== window) {
      window?.parent?.postMessage({ getToken: true }, '*'); // NOSONAR typescript:S2819
    } else {
      console.log('No parent window found, use in standalone mode');
      return isAuthenticatedGuard();
    }

    return fromEvent<{ data: { token: string } }>(window, 'message').pipe(
      timeout(2000),
      first(),
      map((message) => message.data.token),
      switchMap((token) => {
        const isGoogleOAuth2Token = token?.startsWith('ya29.');
        // To log in with firebase, we need to sign in with the token received from the parent window first
        // If the token is a Google access token we can use singInWithCredential
        if (token && isGoogleOAuth2Token) {
          return from(authService.signInWithCredential(token, additionalScopes).then(() => true));
        }
        // For non oauth2 token we use the limited access token because we cannot use the firebase signInWithCredential method (the user will have to sign in later if a fresh new token is needed)
        if (token && useJWTToken && !isGoogleOAuth2Token) {
          authService.setLimitedAccessToken(token, additionalScopes);
          return of(true);
        }
        console.warn('Token not valid from the parent window', token);
        return of(loginUrlTree);
      }),
      catchError(() => {
        console.warn('No token received from the parent window');
        return of(loginUrlTree);
      })
    );
  }
};
